/**
 * FP3-131 · Duplicate-Student-Guard confirmation dialog.
 *
 * Rendered when POST /api/students returns 409
 * `{ code: "duplicate_student_candidates", match_kind, candidates }`.
 *
 * Semantics:
 *   • "hard" match (readable_id / external_id / student_email
 *     collision, OR name-normalized match with matching DOB) →
 *     tone = danger. Two choices only: "Use existing" | "Add anyway".
 *   • "soft" match (name-only, no DOB collision) → tone = warning.
 *     Same two choices.
 *
 * "Use existing" navigates the caller to the existing student's
 * workspace (or invokes an `onUseExisting(id)` callback if the
 * host wants to reroute in-page). "Add anyway" retries the create
 * with `?force=true` — the backend logs the override to
 * `student_snapshots.kind = "fp3131_duplicate_override"`.
 */
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Warning, GraduationCap } from "@phosphor-icons/react";

export default function DuplicateStudentGuardDialog({
  open,
  matchKind,           // "hard" | "soft"
  candidates,          // array from server
  onCancel,
  onForceCreate,       // () => Promise<void>  — retries with force=true
  onUseExisting,       // (studentId) => void  — optional custom router
  submitting,
}) {
  const nav = useNavigate();
  if (!open) return null;
  const isHard = matchKind === "hard";
  const tone = isHard
    ? { border: "#E3C4BE", bg: "#FDF9F8", fg: "#7A1E1E", label: "Duplicate identity" }
    : { border: "#EDD7A6", bg: "#FDF8EC", fg: "#7A5A11", label: "Possible duplicate" };

  const handleUseExisting = (id) => {
    if (onUseExisting) {
      onUseExisting(id);
      return;
    }
    nav(`/students/${id}`);
  };

  return (
    <div
      data-testid="fp3131-guard-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[85vh]"
        style={{ borderTop: `4px solid ${tone.fg}` }}
      >
        <div
          className="p-5 border-b border-[#E5E3DB]"
          style={{ backgroundColor: tone.bg }}
        >
          <h3
            className="text-[16px] font-semibold heading-font flex items-center gap-2"
            style={{ color: tone.fg }}
            data-testid="fp3131-guard-title"
          >
            <Warning size={16} weight="fill" /> {tone.label}
          </h3>
          <p className="text-[12.5px] text-[#3F4756] mt-1 leading-relaxed">
            {isHard
              ? "A student with a matching stable identifier (Frame Practice ID / External ID / email) or the same name + date of birth already exists in this organization. Adding a second record risks the split-history problem FP3-131 was raised to fix. Pick the existing student, or override deliberately."
              : "A student with a similar name already exists in this organization. Same-name siblings are legitimate — just make sure this really is a new student before adding another record."}
          </p>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
            Existing candidate{candidates.length === 1 ? "" : "s"} ({candidates.length})
          </div>
          <ul
            data-testid="fp3131-guard-candidate-list"
            className="rounded-lg border border-[#E5E3DB] divide-y divide-[#E5E3DB]"
          >
            {candidates.map((c) => (
              <li
                key={c.id}
                data-testid={`fp3131-guard-candidate-${c.id}`}
                className="px-3 py-2.5 flex items-start gap-3"
              >
                <GraduationCap size={16} weight="duotone" className="text-[#1E3F33] mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-[#1F2937]">
                    {c.first_name} {c.last_name}
                    {c.status && c.status !== "active" && (
                      <span className="ml-2 text-[10.5px] px-1.5 py-0.5 rounded-full bg-[#EDF2F7] text-[#4A6B8C] font-medium">
                        {c.status}
                      </span>
                    )}
                    {c.is_archived && (
                      <span className="ml-1 text-[10.5px] px-1.5 py-0.5 rounded-full bg-[#F5EFE3] text-[#7A6A2C]">
                        archived
                      </span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-[#4B5563]">
                    Grade {c.grade || "—"} · DOB {c.date_of_birth || "—"}
                    {c.campus && <> · {c.campus}</>}
                  </div>
                  <div className="text-[11px] text-[#6B7280] font-mono break-all">
                    {c.readable_id || c.id}
                    {c.external_id && <> · ext:{c.external_id}</>}
                  </div>
                  <div className="text-[10.5px] text-[#9AA0A6] mt-0.5 italic">
                    matched on: {c.match_reason}
                  </div>
                </div>
                <Button
                  type="button"
                  data-testid={`fp3131-guard-use-existing-${c.id}`}
                  variant="outline"
                  onClick={() => handleUseExisting(c.id)}
                  className="h-8 text-[12px] border-[#1E3F33]/40 text-[#1E3F33] hover:bg-[#E8F0EB]"
                >
                  Use existing
                </Button>
              </li>
            ))}
          </ul>
          {isHard && (
            <p
              data-testid="fp3131-guard-hard-note"
              className="text-[11.5px] text-[#7A1E1E] italic"
            >
              Every override is audited to <code className="font-mono">student_snapshots.kind = "fp3131_duplicate_override"</code>.
            </p>
          )}
        </div>
        <div className="p-4 border-t border-[#E5E3DB] flex items-center justify-end gap-2">
          <Button
            type="button"
            data-testid="fp3131-guard-cancel-btn"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            className="h-9 text-[12.5px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="fp3131-guard-add-anyway-btn"
            onClick={onForceCreate}
            disabled={submitting}
            className={
              isHard
                ? "h-9 bg-[#7A1E1E] hover:bg-[#601515] text-white text-[12.5px] font-medium"
                : "h-9 bg-[#1E3F33] hover:bg-[#173127] text-white text-[12.5px] font-medium"
            }
          >
            {submitting ? "Adding…" : "Add anyway"}
          </Button>
        </div>
      </div>
    </div>
  );
}
